import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import PageHeader from 'components/pageHeader';
import PageDataLoader from 'components/pageDataLoader';
import AddStaffDrawer from 'components/UserDashboardV2/components/addStaffDrawer';

import useResponsive from 'components/UserDashboardV2/hooks/useResponsive';
import useDashboardData from 'components/UserDashboardV2/hooks/useDashboardData';
import RevealCard from 'components/UserDashboardV2/components/RevealCard';
import StaffDistributionCard from 'components/UserDashboardV2/components/StaffDistributionCard';
import ActiveStaffCard from 'components/UserDashboardV2/components/ActiveStaffCard';
import StaffListTable from 'components/UserDashboardV2/components/StaffListTable';
import StudentDistributionCard from 'components/UserDashboardV2/components/StudentDistributionCard';
import ActiveStudentsCard from 'components/UserDashboardV2/components/ActiveStudentsCard';
import StudentListTable from 'components/UserDashboardV2/components/StudentListTable';
import AccessManagement from 'pages/userManagementV3/localComponent/accessManagement';

import {
    setRole,
    setSelectedCourse,
    setStudentWithoutData,
    setUserids,
    setSelectedStandard,
    getDashboard,
    downloadTemplate,
    setUserDetails,
    getAllDeviceApprovalRequests,
} from 'actions/userManagementV2';
import { getStaffStats, getStaffPending } from 'actions/userManagementV3';

import 'components/UserDashboardV2/styles/dashboard.css';
import { COLORS } from 'components/UserDashboardV2/utils/constants';
import { fluid, fluidVh } from 'components/UserDashboardV2/utils/responsive';

// Last known pending count — shown on return from the device requests page
// until this page's own "pending" fetch lands (the shared redux list may still
// hold the Approved / Rejected tab's rows).
let lastDevicePendingCount = null;

const UserDashboardV2 = React.memo(({
    getDashboard,
    dashboardMeta,
    setRole,
    downloadTemplate,
    importStaff,
    getStaffStats,
    staffStats,
    getStaffPending,
    staffPending,
    setUserDetails,
    getAllDeviceApprovalRequests,
    deviceApprovalRequests,
}) => {
    const { isMobile } = useResponsive();
    const history = useHistory();
    const location = useLocation();

    const basePath = location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';

    // Derive initial tab state from pathname
    const getInitialTab = () => {
        if (location.pathname.endsWith('/students')) {
            return "STUDENTS";
        }
        return "STAFF";
    };

    const [activeTab, setActiveTab] = useState(getInitialTab);
    const [showAccessManagement, setShowAccessManagement] = useState(false);
    const [editingStaff, setEditingStaff] = useState(null);

    // GET /user/device-approval-requests?status=pending — just the badge
    // count for the header button; the actual list/grid view now lives on
    // its own full page (/user-management/device-change-requests or /user-management-v2/device-change-requests), which
    // re-fetches this same redux slot for whatever filter it's showing.
    // Refetching "pending" here on mount keeps the badge itself always
    // reflecting the pending count specifically, same as registrationsCount
    // does for staff via getStaffPending.
    const [devicePendingLoaded, setDevicePendingLoaded] = useState(false);
    useEffect(() => {
        if (getAllDeviceApprovalRequests) {
            getAllDeviceApprovalRequests({ status: 'pending' }, () => setDevicePendingLoaded(true));
        }
    }, [getAllDeviceApprovalRequests]);

    const devicePendingList = Array.isArray(deviceApprovalRequests) ? deviceApprovalRequests : (deviceApprovalRequests?.data || []);
    // Only trust the shared list once our own pending fetch has landed.
    const devicePendingCount = devicePendingLoaded ? devicePendingList.length : (lastDevicePendingCount ?? 0);
    useEffect(() => {
        if (devicePendingLoaded) lastDevicePendingCount = devicePendingList.length;
    }, [devicePendingLoaded, devicePendingList.length]);

    const {
        pageLoader,
        addUserModal,
        setAddUserModal,
    } = useDashboardData(dashboardMeta, getDashboard, downloadTemplate, importStaff);

    const [statsLoading, setStatsLoading] = useState(false);
    const [staffDistributionStats, setStaffDistributionStats] = useState(null);
    const [staffStatsRefreshTrigger, setStaffStatsRefreshTrigger] = useState(0);
    const [studentStatsRefreshTrigger, setStudentStatsRefreshTrigger] = useState(0);
    const currentStaffRangeRef = useRef({});

    useEffect(() => {
        if (staffStats) {
            setStaffDistributionStats(staffStats);
        }
    }, [staffStats]);

    useEffect(() => {
        if (getStaffStats) {
            getStaffStats();
        }
        if (getStaffPending) {
            getStaffPending();
        }
    }, [getStaffStats, getStaffPending]);

    // Passed down to StaffDistributionCard -> DateRangeFilterPopover's
    // onApply — refetches /userv2/staff/stats with the range the user just
    // picked (all/last30/custom). Stores the filtered result independently
    // in staffDistributionStats so ActiveStaffCard's year changes do not
    // overwrite it.
    const handleStaffRangeChange = (params) => {
        currentStaffRangeRef.current = params || {};
        if (getStaffStats) {
            getStaffStats({ ...(params || {}), skipRedux: true }, (response) => {
                const payload = response?.data ?? response;
                if (payload) {
                    setStaffDistributionStats(payload);
                }
            });
        }
    };

    const handleStaffStatusChange = useCallback((newStaffOrStats) => {
        // If passed a stats object directly from stats API, apply it immediately
        if (newStaffOrStats && typeof newStaffOrStats === "object" && (newStaffOrStats.byRole !== undefined || (newStaffOrStats.total !== undefined && newStaffOrStats.teachers !== undefined))) {
            setStaffDistributionStats(newStaffOrStats);
            return;
        }

        // Optimistically increment staff distribution stats when a staff member is added
        if (newStaffOrStats && typeof newStaffOrStats === "object") {
            const rawRole = (Array.isArray(newStaffOrStats.role) ? newStaffOrStats.role[0] : (newStaffOrStats.role || newStaffOrStats.roles?.[0] || "")).toLowerCase();
            const isOp = rawRole.includes("operation");
            const isTeacher = rawRole.includes("teacher");

            setStaffDistributionStats((prev) => {
                const currentStats = prev || staffStats;
                if (!currentStats) return prev;
                const prevTotal = Number(currentStats.totalStaff ?? currentStats.total ?? 0);
                const newTotal = prevTotal + 1;
                const prevTeacher = Number(currentStats.byRole?.Teacher ?? currentStats.byRole?.teacher ?? currentStats.teachers?.count ?? 0);
                const prevOp = Number(currentStats.byRole?.Operation ?? currentStats.byRole?.operation ?? currentStats.operations?.count ?? 0);

                return {
                    ...currentStats,
                    total: newTotal,
                    totalStaff: newTotal,
                    byRole: {
                        ...(currentStats.byRole || {}),
                        Teacher: isTeacher ? prevTeacher + 1 : prevTeacher,
                        Operation: isOp ? prevOp + 1 : prevOp,
                    },
                };
            });
        }

        // Also refresh staff distribution stats with the currently applied range filter
        if (getStaffStats) {
            const rangeParams = currentStaffRangeRef.current && Object.keys(currentStaffRangeRef.current).length > 0
                ? { ...currentStaffRangeRef.current }
                : undefined;
            getStaffStats(rangeParams, (response) => {
                const payload = response?.data ?? response;
                if (payload) {
                    setStaffDistributionStats(payload);
                }
            });
        }
        if (getStaffPending) {
            getStaffPending();
        }

        // Trigger ActiveStaffCard to refetch year-specific active staff trend
        setTimeout(() => {
            setStaffStatsRefreshTrigger((prev) => prev + 1);
        }, 500);
    }, [getStaffStats, getStaffPending, staffStats]);

    const handleStudentStatusChange = useCallback(() => {
        setStudentStatsRefreshTrigger((prev) => prev + 1);
    }, []);

    // Synchronize url pathname changes with the active tab state
    useEffect(() => {
        if (location.pathname.endsWith('/students') && activeTab !== "STUDENTS") {
            setActiveTab("STUDENTS");
        } else if (location.pathname.endsWith('/staffs') && activeTab !== "STAFF") {
            setActiveTab("STAFF");
        } else if (location.pathname === basePath) {
            history.replace(`${basePath}/staffs`);
        }
    }, [location.pathname, activeTab, history, basePath]);

    // GET /userv2/staff/pending — badge count = list length.
    const pendingList = Array.isArray(staffPending) ? staffPending : (staffPending?.items || staffPending?.data || []);
    const pendingCount = pendingList.length;

    // if (pageLoader) {
    //     return <PageDataLoader visible={pageLoader} />;
    // }

    if (showAccessManagement) {
        return <AccessManagement onBack={() => setShowAccessManagement(false)} />;
    }

    const bodyStyle = {
        height: isMobile ? "auto" : "100%",
        width: "calc(100% + 30px)",
        margin: -20,
        padding: isMobile ? 12 : "20px 8px 0px 8px",
        overflow: "visible",
        marginTop: 0,
        display: "flex",
        flexDirection: "column",
    };

    const pageHeader = document.getElementById("pageHeader");
    const pageHeaderHeight = pageHeader?.clientHeight || 60;

    const actions = [
        <div
            key="header-actions"
            className="dv2-header-actions"
            style={{
                display: "flex",
                alignItems: "center",
                gap: fluid(14),
                flexWrap: "wrap",
            }}
        >
            {activeTab === "STAFF" && (
                <button
                    type="button"
                    className="manage-access-btn"
                    onClick={() => setShowAccessManagement(true)}
                    style={{
                        height: fluid(40),
                        boxSizing: "border-box",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: fluid(16),
                        borderRadius: fluid(12),
                        border: "none",
                        backgroundColor: COLORS.primary || "#594099",
                        color: "#FFFFFF",
                        fontWeight: 700,
                        padding: `0 ${fluid(32)}`,
                        cursor: "pointer",
                        textTransform: "uppercase",
                    }}
                >
                    MANAGE ACCESS
                </button>
            )}

            <button
                type="button"
                onClick={() => {
                    const role = activeTab === "STAFF" ? "staff" : "student";
                    history.push({
                        pathname: `${basePath}/device-change-requests`,
                        search: `?role=${role}`,
                        state: { from: role, role },
                    });
                }}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: fluid(10),
                    height: fluid(40),
                    padding: `0 ${fluid(32)}`,
                    boxSizing: "border-box",
                    border: `2px solid ${COLORS.primary}`,
                    borderRadius: fluid(12),
                    backgroundColor: "transparent",
                    color: COLORS.primary,
                    fontSize: fluid(16),
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0,
                    lineHeight: "normal",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    flexShrink: 0,
                }}
            >
                Device change request ({activeTab === "STAFF" ? 0 : devicePendingCount})
            </button>

            <div
                className="dv2-staff-toggle"
                style={{
                    display: "flex",
                    width: fluid(291),
                    height: fluid(40),
                    boxSizing: "border-box",
                    border: `2px solid ${COLORS.primary}`,
                    borderRadius: fluid(12),
                    overflow: "hidden",
                    flexShrink: 0,
                }}
            >
                <button
                    type="button"
                    onClick={() => {
                        setRole("userRole", "Staff");
                        setActiveTab("STAFF");
                        history.push(`${basePath}/staffs`);
                    }}
                    style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: fluid(10),
                        padding: `0 ${fluid(32)}`,
                        border: "none",
                        borderTopRightRadius: fluid(12),
                        borderBottomRightRadius: fluid(12),
                        backgroundColor: activeTab === "STAFF" ? COLORS.primary : "transparent",
                        color: activeTab === "STAFF" ? "#FFFFFF" : COLORS.primary,
                        fontSize: fluid(16),
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0,
                        lineHeight: "normal",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                    }}
                >
                    Staff
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setRole("userRole", "Student");
                        setActiveTab("STUDENTS");
                        history.push(`${basePath}/students`);
                    }}
                    style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: fluid(10),
                        padding: `0 ${fluid(32)}`,
                        border: "none",
                        borderTopLeftRadius: fluid(12),
                        borderBottomLeftRadius: fluid(12),
                        backgroundColor: activeTab === "STUDENTS" ? COLORS.primary : "transparent",
                        color: activeTab === "STUDENTS" ? "#FFFFFF" : COLORS.primary,
                        fontSize: fluid(16),
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0,
                        lineHeight: "normal",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                    }}
                >
                    Students
                </button>
            </div>
        </div>
    ];

    return (
        <div className="dashboard-v2-page" style={bodyStyle}>
            <PageDataLoader visible={statsLoading} />
            <div id="pageHeader">
                <PageHeader
                    title='User Management'
                    actions={actions}
                    // dv2-outer-scroll (below) adds extra padding of its own
                    // on top of the page's base padding, so the analytics
                    // cards sit further in on each side than this header
                    // does by default — these margins match that inset so
                    // the title lines up with StaffDistributionCard and the
                    // Staff/Students toggle lines up with ActiveStaffCard.
                    titleStyle={{ fontWeight: 700, fontSize: "1.75rem", color: "#191919", fontFamily: "Roboto, sans-serif", marginLeft: isMobile ? 0 : fluid(12) }}
                    actionsStyle={isMobile ? undefined : { flex: "0 0 auto", width: "auto", marginRight: fluid(12) }}
                />
            </div>

            <div className="dv2-outer-scroll" style={{
                height: isMobile ? "auto" : `calc(100% - ${pageHeaderHeight}px)`,
                overflowY: isMobile ? "visible" : "auto",
                overflowX: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? "16px" : fluid(22),
                width: "100%",
                marginTop: isMobile ? "12px" : fluid(4),
                padding: isMobile ? 0 : `${fluid(20)} ${fluid(8)} 0px ${fluid(8)}`,
                flex: 1,
                minHeight: 0,
            }}>
                <div className="dv2-analytics-row" style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? "16px" : fluid(22),
                    width: "100%",
                    flexShrink: 0,
                    marginTop: isMobile ? 0 : fluid(10),
                }}>
                    <RevealCard className="dv2-analytics-card" order={0} style={{ flex: isMobile ? "1 1 auto" : (activeTab === "STAFF" ? "0.46" : "0.70"), minWidth: 0, height: isMobile ? "auto" : "auto", minHeight: isMobile ? "auto" : fluidVh(440) }}>
                        {activeTab === "STAFF" ? (
                            <StaffDistributionCard isMobile={isMobile} staffStats={staffDistributionStats || staffStats} onRangeChange={handleStaffRangeChange} />
                        ) : (
                            <StudentDistributionCard isMobile={isMobile} />
                        )}
                    </RevealCard>

                    <RevealCard className="dv2-analytics-card" order={1} style={{ flex: isMobile ? "1 1 auto" : (activeTab === "STAFF" ? "0.54" : "0.30"), minWidth: 0, height: isMobile ? "auto" : "auto", minHeight: isMobile ? "auto" : fluidVh(440) }}>
                        {activeTab === "STAFF" ? (
                            <ActiveStaffCard isMobile={isMobile} refreshTrigger={staffStatsRefreshTrigger} />
                        ) : (
                            <ActiveStudentsCard isMobile={isMobile} refreshTrigger={studentStatsRefreshTrigger} />
                        )}
                    </RevealCard>
                </div>

                <div style={{ width: "100%", minWidth: 0, flexShrink: 0 }}>
                    {activeTab === "STAFF" ? (
                        <StaffListTable
                            isMobile={isMobile}
                            activeTab={activeTab}
                            onAddStaff={() => setAddUserModal(true)}
                            onOpenRegistrations={() => history.push(`${basePath}/staff-registration-requests`)}
                            // StaffListTable itself now seeds userRowDetails and
                            // navigates to /user-management/profile on "View" —
                            // this callback is just an extra hook for the parent,
                            // not the thing that makes View work.
                            onViewProfile={() => { }}
                            onEditDetails={(row) => setEditingStaff(row)}
                            registrationsCount={pendingCount}
                            onAddAccess={() => setShowAccessManagement(true)}
                            onStaffStatusChange={handleStaffStatusChange}
                            staffStats={staffDistributionStats || staffStats}
                        />
                    ) : (
                        <StudentListTable
                            isMobile={isMobile}
                            onAddStudent={() => history.push(`${basePath}/add-student`)}
                            onStudentStatusChange={handleStudentStatusChange}
                        />
                    )}
                </div>
            </div>

            <AddStaffDrawer
                visible={addUserModal || !!editingStaff}
                editingStaff={editingStaff}
                onStaffAdded={handleStaffStatusChange}
                onClose={() => {
                    setAddUserModal(false);
                    setEditingStaff(null);
                }}
            />
        </div>
    );
});

UserDashboardV2.displayName = 'UserDashboardV2';

const mapStateToProps = (state) => {
    const { dashboardMeta, deviceApprovalRequests } = state.userManagementV2;
    const { staffStats, staffPending } = state.userManagementV3;
    return { dashboardMeta, staffStats, staffPending, deviceApprovalRequests };
};

const mapDispatchToProps = (dispatch) => ({
    getDashboard: (params, callback) => dispatch(getDashboard(params, callback)),
    getStaffStats: (params, callback) => dispatch(getStaffStats(params, callback)),
    getStaffPending: (params, callback) => dispatch(getStaffPending(params, callback)),
    setUserDetails: (res) => dispatch(setUserDetails(res)),
    getAllDeviceApprovalRequests: (params, callback) => dispatch(getAllDeviceApprovalRequests(params, callback)),
    setRole: (key, val) => dispatch(setRole(key, val)),
    setSelectedCourse: (res) => dispatch(setSelectedCourse(res)),
    setStudentWithoutData: (res) => dispatch(setStudentWithoutData(res)),
    setUserids: (userIds) => dispatch(setUserids(userIds)),
    setSelectedStandard: (res) => dispatch(setSelectedStandard(res)),
    downloadTemplate: (callback) => dispatch(downloadTemplate(callback)),
    importStaff: (data, successCallback, errorCallback) => {
        if (typeof importStaff !== 'undefined') {
            return dispatch(importStaff(data, successCallback, errorCallback));
        }
        console.warn("importStaff action is not defined in scope");
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(UserDashboardV2);
